import { ArrowLeftOutlined } from '@ant-design/icons';
import { Alert, Button, Layout, message } from 'antd';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { getPost } from '../../../services/postApi';
import { SEO_DEFAULTS } from '../../../utils/seoConfig';
import { generateArticleSchema } from '../../../utils/structuredData';
import FullPageMediaViewer from '../../MediaViewer/FullPageMediaViewer';
import { PageSkeleton } from '../../Common/Skeletons';
import FeedPost from '../FeedPost/FeedPost';
import './SinglePostPage.css';

const { Content } = Layout;

/**
 * Helper to extract plain text preview from post content
 * Strips markdown and limits length for meta description
 */
const getContentPreview = (content, maxLength = 160) => {
  if (!content) return SEO_DEFAULTS.description;
  // Strip basic markdown syntax
  const plainText = content
    .replace(/[#*_`~\[\]]/g, '')
    .replace(/\n+/g, ' ')
    .trim();
  if (plainText.length <= maxLength) return plainText;
  return plainText.substring(0, maxLength - 3) + '...';
};

/**
 * Get the best image URL for Open Graph from post media
 */
const getOGImage = (post) => {
  if (!post?.media_files?.length) return SEO_DEFAULTS.image;
  // Find first image type media
  const imageMedia = post.media_files.find(m => 
    m.file_type === 'image' || 
    m.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i)
  );
  return imageMedia?.url || post.media_files[0]?.url || SEO_DEFAULTS.image;
};

const SinglePostPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { currentUser } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerVisible, setViewerVisible] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getPost(postId);
        setPost(response.data);
      } catch (err) {
        console.error('Error fetching post:', err);
        
        // Handle both auth and non-auth errors
        if (err.response?.status === 401 && !currentUser) {
          // For unauthenticated users, this might be a private post
          setError({
            message: 'Authentication Required',
            description: 'This post may require authentication to view.',
            showLogin: true
          });
        } else if (err.response?.status === 404) {
          setError({
            message: 'Post Not Found',
            description: "The post you're looking for doesn't exist or has been removed.",
            showLogin: false
          });
        } else {
          setError({
            message: 'Error Loading Post',
            description: 'An error occurred while loading the post. Please try again.',
            showLogin: false
          });
        }
      } finally {
        setLoading(false);
      }
    };

    if (postId) {
      fetchPost();
    }
  }, [postId, currentUser]);

  const handleMediaClick = (post, mediaIndex) => {
    // Now support all media types, not just images
    if (post.media_files && post.media_files.length > 0) {
      setSelectedMediaIndex(mediaIndex);
      setViewerVisible(true);
    }
  };

  const handleCloseMediaViewer = () => {
    setViewerVisible(false);
    setSelectedMediaIndex(0);
  };

  const handleBackToFeed = () => {
    navigate('/posts');
  };

  const handleLogin = () => {
    // Save current location for redirect after login
    sessionStorage.setItem('redirect_after_login', `/posts/${postId}`);
    navigate('/login');
  };

  // SEO data for the post
  const seoTitle = post?.title || 'Post';
  const seoDescription = getContentPreview(post?.content);
  const seoImage = getOGImage(post);
  const seoUrl = `${SEO_DEFAULTS.url}/posts/${postId}`;

  // Generate structured data for article
  const articleSchema = post ? generateArticleSchema({
    ...post,
    id: postId,
  }) : null;

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Loading... | {SEO_DEFAULTS.siteName}</title>
        </Helmet>
        <Layout className={`single-post-page page-gradient-background ${isDarkMode ? 'dark' : 'light'}`}>
          <Content className="single-post-content">
            <PageSkeleton rows={8} avatar={true} />
          </Content>
        </Layout>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Helmet>
          <title>Post Not Found | {SEO_DEFAULTS.siteName}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <Layout className={`single-post-page page-gradient-background ${isDarkMode ? 'dark' : 'light'}`}>
          <Content className="single-post-content">
            <div className="single-post-error">
              <Alert
                message={error.message}
                description={error.description}
                type="error"
                showIcon
                action={
                  <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                    <Button type="primary" onClick={handleBackToFeed}>
                      Go to Posts Feed
                    </Button>
                    {error.showLogin && !currentUser && (
                      <Button onClick={handleLogin}>
                        Login to View
                      </Button>
                    )}
                  </div>
                }
              />
            </div>
          </Content>
        </Layout>
      </>
    );
  }

  return (
    <>
      {/* SEO Meta Tags - Dynamic per post */}
      <Helmet>
        <title>{seoTitle} | {SEO_DEFAULTS.siteName}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={seoUrl} />

        {/* Indexing policy: only CellCog's OWN product-update posts are indexable.
            User-generated "chat" posts get noindex — they come from many users with
            wildly varied topics that muddy CellCog's brand narrative in search. They
            still render + share fine (OG tags below are unaffected); we just keep them
            out of the search index. Mirrors the sitemap filter in py/cellcog/api/seo.py
            (major_update/minor_update only). */}
        {!['major_update', 'minor_update'].includes(post.post_type) && (
          <meta name="robots" content="noindex, follow" />
        )}

        {/* Open Graph - Critical for social sharing */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content={SEO_DEFAULTS.siteName} />
        
        {/* Article-specific Open Graph */}
        {post?.created_at && (
          <meta property="article:published_time" content={post.created_at} />
        )}
        {post?.updated_at && (
          <meta property="article:modified_time" content={post.updated_at} />
        )}
        <meta property="article:author" content="CellCog" />
        {post?.tags?.map((tag, index) => (
          <meta key={index} property="article:tag" content={tag} />
        ))}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <meta name="twitter:site" content={SEO_DEFAULTS.twitterHandle} />
        
        {/* Structured Data */}
        {articleSchema && (
          <script type="application/ld+json">
            {JSON.stringify(articleSchema)}
          </script>
        )}
      </Helmet>

      <Layout className={`single-post-page page-gradient-background ${isDarkMode ? 'dark' : 'light'}`}>
        <Content className="single-post-content">
          <div className="single-post-wrapper">
            {/* Simple Back Button */}
            <div className="single-post-navigation">
              <Button
                size='large'
                icon={<ArrowLeftOutlined />}
                onClick={handleBackToFeed}
              >
                Back to Posts
              </Button>
            </div>

            {/* Post Content - Keep original card styling */}
            <div className="single-post-container">
              {post && (
                <FeedPost
                  post={post}
                  onMediaClick={handleMediaClick}
                  isDarkMode={isDarkMode}
                />
              )}
            </div>
          </div>
        </Content>

        {/* Media Viewer - now supports all media types */}
        {viewerVisible && post && post.media_files && post.media_files.length > 0 && (
          <FullPageMediaViewer
            mediaFiles={post.media_files}
            initialIndex={selectedMediaIndex}
            onClose={handleCloseMediaViewer}
          />
        )}
      </Layout>
    </>
  );
};

export default SinglePostPage;
